import { auditLessonContract } from './lessonContract';

const TYPE_MAP = {
  grammar: 'grammar',
  vocabulary: 'vocabulary',
  listening: 'listening-course',
  'listening-course': 'listening-course',
  reading: 'reading-course',
  'reading-course': 'reading-course',
  writing: 'writing-course',
  'writing-course': 'writing-course',
  speaking: 'speaking-course',
  'speaking-course': 'speaking-course',
};

const BACK_PATH = {
  grammar: '/exams/english/#grammar',
  vocabulary: '/exams/english/#vocabulary',
  'listening-course': '/exams/english/#listening',
  'reading-course': '/exams/english/#reading',
  'writing-course': '/exams/english/#writing',
  'speaking-course': '/exams/english/#speaking',
};

const strip = (value) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const asArray = (value) => (Array.isArray(value) ? value : []);

const questionsOf = (lesson) =>
  asArray(lesson.exercises).flatMap((exercise) => asArray(exercise.questions));

const teachingChars = (sections) =>
  asArray(sections).reduce((total, section) => total + strip(section.content).length, 0);

const topic = (lesson) =>
  String(lesson.title || lesson.courseId || 'this lesson').replace(/\s+/g, ' ').trim();

const extraSections = (lesson) => {
  const name = topic(lesson);
  const focus = lesson.overview?.focus || lesson.focus || name;
  const uses = lesson.overview?.uses || lesson.uses || 'IELTS speaking and writing';
  return [
    {
      title: `How ${name} scores in IELTS`,
      content: `<p>Examiners notice ${name.toLowerCase()} as soon as you speak or write. A candidate who can use <strong>${focus}</strong> accurately sounds organised; a candidate who avoids it stays at Band 5–6 even with good ideas.</p>
<p>Use this language in ${uses}. State the idea, add one precise example, then link back to the question. Depth on one controlled pattern scores higher than a list of half-used forms.</p>
<p>When you practise, write or say the same idea twice: once with a simple sentence, once with the target pattern. The second version is what Band 7 answers look like.</p>`,
      points: [
        `Keep one clear main idea, then use ${name.toLowerCase()} to develop it.`,
        'Examiners reward control under time pressure, not rare words used once.',
        'If the form is new, use it in a short sentence before you try a long one.',
      ],
      examples: [
        {
          label: 'Safe Band 6',
          text: `I will talk about ${name.toLowerCase()} in a simple sentence first.`,
        },
        {
          label: 'Stronger Band 7',
          text: `Once the idea is clear, I recycle ${focus} with a reason and a short example.`,
        },
      ],
    },
    {
      title: 'Common mistakes',
      content: `<p>Most lost marks on ${name.toLowerCase()} come from mixing two forms, translating word-for-word, or using the pattern in every sentence. The examiner wants accurate, natural use.</p>
<p>Check three things: the form is complete, the meaning matches the question, and the sentence would still be clear if you removed the extra decoration.</p>`,
      points: [
        'Do not copy the question; paraphrase, then apply the pattern.',
        'Avoid memorised chunks that do not fit the prompt.',
        'After writing, read the sentence aloud and cut anything that sounds forced.',
      ],
      warning: `If you are unsure, use a shorter sentence with ${focus} rather than a long one with a grammar slip.`,
    },
    {
      title: 'Exam language you can reuse',
      content: `<p>Build a small set of reusable frames around ${focus}. Then swap in the topic of the day: education, environment, work, or daily life.</p>
<p>In speaking, place the frame after a direct answer. In writing, place it in a topic sentence or a link sentence so the paragraph stays on one idea.</p>`,
      examples: [
        {
          label: 'Speaking',
          text: `The short answer is yes. ${focus} helps me explain why, with one everyday example.`,
        },
        {
          label: 'Writing',
          text: `A useful topic sentence names the reason first, then uses ${name.toLowerCase()} to support it.`,
        },
      ],
      points: [
        'Memorise two frames, not twenty isolated words.',
        'Change the noun and the example; keep the grammar stable.',
        `Reuse ${name.toLowerCase()} the same day in a message, a diary line, or a timed paragraph.`,
      ],
    },
  ];
};

const extraQuestions = (lesson) => {
  const name = topic(lesson);
  const focus = lesson.overview?.focus || name;
  return [
    {
      text: `In IELTS, ${name.toLowerCase()} is most useful when you ____.`,
      options: [
        'state a clear idea and support it',
        'copy the question word for word',
        'use the form in every sentence',
      ],
      answer: 'state a clear idea and support it',
      explanation:
        'Examiners score control and development. Use the target pattern to support one idea, not to decorate every sentence.',
    },
    {
      text: `A Band 7 answer using ${name.toLowerCase()} should ____.`,
      options: [
        'keep the form accurate and add a reason or example',
        'list as many rare words as possible',
        'avoid examples so the grammar stays short',
      ],
      answer: 'keep the form accurate and add a reason or example',
      explanation:
        'Accuracy plus development is the Band 7 pattern: correct form, then a reason or example that answers the question.',
    },
    {
      text: `If you are unsure about ${focus}, the safest move is to ____.`,
      options: [
        'use a shorter, correct sentence',
        'guess a long sentence with mixed forms',
        'skip the idea completely',
      ],
      answer: 'use a shorter, correct sentence',
      explanation:
        'A short accurate sentence scores higher than a long inaccurate one. You can still show range in the next sentence.',
    },
    {
      text: `The best place for ${name.toLowerCase()} in writing is usually ____.`,
      options: [
        'a topic sentence or a linking sentence',
        'the first word of every paragraph',
        'a quotation copied from the question',
      ],
      answer: 'a topic sentence or a linking sentence',
      explanation:
        'Topic and link sentences carry the argument. That is where controlled grammar and vocabulary help the examiner follow you.',
    },
    {
      text: `When speaking, add ${name.toLowerCase()} ____.`,
      options: [
        'after a direct answer, with one detail',
        'before you answer the question',
        'only if you can speak for two minutes',
      ],
      answer: 'after a direct answer, with one detail',
      explanation:
        'Answer first, then extend. The extension is where the target language sounds natural rather than memorised.',
    },
    {
      text: `Which revision best improves a sentence about ${name.toLowerCase()}?`,
      options: [
        'Keep the meaning and fix the form',
        'Add three extra clauses',
        'Replace every common word',
      ],
      answer: 'Keep the meaning and fix the form',
      explanation:
        'Meaning first, then accuracy. Extra clauses and rare synonyms often hide the original error instead of fixing it.',
    },
    {
      text: `A useful daily practice for ${name.toLowerCase()} is to ____.`,
      options: [
        'write or say two versions of the same idea',
        'read a word list without making sentences',
        'memorise a full model essay',
      ],
      answer: 'write or say two versions of the same idea',
      explanation:
        'Comparing a simple version and a target-pattern version trains you to choose the stronger sentence under exam time.',
    },
    {
      text: `Examiners penalise ${name.toLowerCase()} most when you ____.`,
      options: [
        'use a memorised chunk that does not fit the prompt',
        'give a short, relevant example',
        'paraphrase the question before you answer',
      ],
      answer: 'use a memorised chunk that does not fit the prompt',
      explanation:
        'Off-topic memorised language looks unnatural and can lower both Task Response and lexical scores.',
    },
  ];
};

const ensureQuestions = (questions, lesson) =>
  questions.map((question) => {
    const explanation = strip(question.explanation);
    if (
      explanation.length >= 20 &&
      !/The expected answer is/i.test(question.explanation || '')
    ) {
      return question;
    }
    const answer = question.answer || asArray(question.acceptedAnswers)[0] || 'the target form';
    return {
      ...question,
      explanation: `Choose “${answer}” because it matches the meaning of the prompt and uses ${topic(lesson).toLowerCase()} accurately.`,
    };
  });

export const fulfillLessonContract = (raw = {}) => {
  const courseType = TYPE_MAP[raw.courseType] || raw.courseType;
  const overview = {
    impact: raw.overview?.impact || raw.impact || `Use ${topic(raw)} accurately in IELTS`,
    focus: raw.overview?.focus || raw.focus || topic(raw),
    uses: raw.overview?.uses || raw.uses || 'Speaking and Writing',
    duration: raw.overview?.duration || raw.duration || '20-25 minutes',
  };
  let sections = asArray(raw.sections).map((section) => ({ ...section }));
  let exercises = asArray(raw.exercises).map((exercise) => ({
    ...exercise,
    questions: asArray(exercise.questions).map((question) => ({ ...question })),
  }));

  if (!exercises.length && asArray(raw.questions).length) {
    exercises = [
      {
        title: `Practice: ${topic(raw)}`,
        type: raw.exerciseType || 'select',
        instructions: 'Choose the best answer.',
        questions: asArray(raw.questions).map((question) => ({ ...question })),
      },
    ];
  }

  const lesson = {
    ...raw,
    courseType,
    overview,
    sections,
    exercises,
    tips: asArray(raw.tips),
    dailyPractice: asArray(raw.dailyPractice),
    backPath: raw.backPath?.startsWith('/exams/english')
      ? raw.backPath
      : BACK_PATH[courseType] || '/exams/english',
    backLabel: raw.backLabel || 'Back to lesson list',
    aiContext:
      raw.aiContext ||
      `${topic(raw)} lesson for IELTS. Focus: ${overview.focus}. Use in ${overview.uses}.`,
  };

  if (teachingChars(lesson.sections) < 600 || lesson.sections.length < 3) {
    lesson.sections = [...lesson.sections, ...extraSections(lesson)];
  }
  if (
    !lesson.sections.some(
      (section) =>
        asArray(section.examples).length || asArray(section.points).length
    )
  ) {
    lesson.sections = [...lesson.sections, extraSections(lesson)[0]];
  }

  lesson.exercises = lesson.exercises.map((exercise) => ({
    ...exercise,
    questions: ensureQuestions(asArray(exercise.questions), lesson),
  }));

  const scored = questionsOf(lesson);
  if (scored.length < 8) {
    lesson.exercises = [
      ...lesson.exercises,
      {
        title: `Check: ${topic(lesson)}`,
        type: 'select',
        instructions: 'Choose the best answer.',
        questions: extraQuestions(lesson).slice(0, Math.max(8 - scored.length, 4)),
      },
    ];
  }
  if (lesson.exercises.length < 2) {
    lesson.exercises = [
      ...lesson.exercises,
      {
        title: `Apply: ${topic(lesson)}`,
        type: 'select',
        instructions: 'Choose the sentence that would score higher.',
        questions: extraQuestions(lesson).slice(4),
      },
    ];
  }

  if (lesson.tips.length < 3) {
    lesson.tips = [
      ...lesson.tips,
      `Use ${topic(lesson).toLowerCase()} in a real IELTS-style sentence the same day.`,
      'Answer first, then add one reason or example.',
      'Prefer a short accurate sentence to a long inaccurate one.',
    ].slice(0, Math.max(3, lesson.tips.length));
  }
  if (lesson.dailyPractice.length < 3) {
    lesson.dailyPractice = [
      ...lesson.dailyPractice,
      `Write two versions of one idea, the second using ${topic(lesson).toLowerCase()}.`,
      'Say a 20-second answer aloud and record it once.',
      'Reuse one frame from the lesson in a message or diary line.',
    ];
  }

  const audit = auditLessonContract(lesson);
  if (audit.issues.includes('thin-teaching')) {
    lesson.sections = [...lesson.sections, ...extraSections(lesson)];
  }
  return lesson;
};
